import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import {
  SubscriptionPlanKeyV1,
  SubscriptionPlanKeyV2,
  SubscriptionPlanKeyV2Analysis,
} from '@client/features/billing/config';
import {
  SubscriptionPlanBundle,
  SubscriptionPlanInterval,
  SubscriptionPlanLevel,
} from '@client/features/subscription-plan/types';
import { toEnum } from '@client/core/data/concerns/parses-from-json/to-enum';
import { SoftwareFeature } from '@client/features/software-feature/types';

export class SubscriptionPlan {
  constructor(
    public readonly key: SubscriptionPlanKeyV1 | SubscriptionPlanKeyV2 | SubscriptionPlanKeyV2Analysis,
    public readonly bundle: SubscriptionPlanBundle,
    public readonly amount: number,
    public readonly level: SubscriptionPlanLevel,
    public readonly interval: SubscriptionPlanInterval,
    public readonly name: string,
    public readonly fullName: string,
    public readonly description: string,
    public readonly featureAccess: Partial<Record<SoftwareFeature, boolean>>,
    public readonly features: { text: string; enabled: boolean }[],
    public readonly userLimit: number,
    public readonly projectLimit: number,
    public readonly faIcon: Array<string>,
  ) {}

  /**
   * Check whether this Plan offers its Users to use the given feature.
   *
   * This is mostly used for making UI decisions about whether to show/hide
   * certain UI elements.
   *
   * This is NOT for checking whether a specific User has permission to use
   * this feature. For that see composable useFeatureGuard(SoftwareFeature)
   */
  supportsFeature(feature: SoftwareFeature): boolean {
    return this.featureAccess[feature] ?? false;
  }

  get projectLimitText(): string {
    return this.projectLimit === 9999 ? 'Unlimited' : String(this.projectLimit);
  }
}

defineTransform(SubscriptionPlan, (json, { fromSnakeCaseKeys }) => {
  return {
    ...fromSnakeCaseKeys(),
    bundle: json.bundle ? toEnum(SubscriptionPlanBundle, json.bundle) : null,
    features: json.features.map((feature: any) => ({
      text: feature.text,
      enabled: feature.enabled,
    })),
  };
});
